import { useState } from "react";
import { useEffect } from "react";
import { api, errMsg } from "@/lib/api";
import { fmt, today } from "@/lib/billing";
import { FundingInvoiceSelect } from "@/components/FundingInvoiceSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export function PaymentDialog({ invoice, endpoint, label, onClose, onDone }) {
  const empty = { amount: invoice ? String(invoice.balance) : "", date: today(), payment_mode: "cash", reference: "", note: "", invoice_id: "" };
  const [f, setF] = useState(empty);
  const [funds, setFunds] = useState(null);
  const isVendor = endpoint.includes("vendor");

  useEffect(() => {
    if (invoice) {
      setF({ ...empty, amount: String(invoice.balance) });
      if (isVendor) api.get("/funds/summary").then((r) => setFunds(r.data)).catch(() => {});
    }
    // eslint-disable-next-line
  }, [invoice]);

  const submit = async (e) => {
    e.preventDefault();
    if (isVendor && !f.invoice_id) return toast.error("Select the Sales Invoice this payment is made from");
    try {
      const r = await api.post(`${endpoint}/${invoice.id}/payments`, { ...f, amount: parseFloat(f.amount) });
      toast.success(r.data.status === "paid" ? "Fully settled" : `${label} recorded`);
      onDone();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border">
        <DialogHeader><DialogTitle>{label}</DialogTitle></DialogHeader>
        {invoice && (
          <form onSubmit={submit} className="space-y-4">
            <div className="rounded-lg bg-white/5 p-3 text-sm flex justify-between">
              <span className="font-medium">{invoice.invoice_no} · {invoice.party_name || invoice.vendor_name}</span>
              <span className="text-amber-400 font-mono-sku" data-testid="inv-pay-balance">Due {fmt(invoice.balance)}</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Amount (₹)</Label>
                <Input data-testid="inv-payment-amount" type="number" step="0.01" min="0.01" max={invoice.balance} required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></div>
              <div className="space-y-2"><Label>Date</Label>
                <Input data-testid="inv-payment-date" type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Mode</Label>
                <Select value={f.payment_mode} onValueChange={(v) => setF({ ...f, payment_mode: v })}>
                  <SelectTrigger data-testid="inv-payment-mode"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="bank">Bank Transfer</SelectItem><SelectItem value="cheque">Cheque</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>Reference</Label>
                <Input data-testid="inv-payment-ref" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="UTR / Cheque no." /></div>
            </div>
            <div className="space-y-2"><Label>Note</Label>
              <Input data-testid="inv-payment-note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Optional" /></div>
            {isVendor && <FundingInvoiceSelect funds={funds} value={f.invoice_id} onChange={(v) => setF({ ...f, invoice_id: v })} testid="vendor-payment-invoice-select" />}
            {invoice.payments?.length > 0 && (
              <div className="space-y-1"><p className="text-xs uppercase text-muted-foreground font-mono-sku">History</p>
                {invoice.payments.map((p) => (
                  <div key={p.id} className="flex justify-between text-xs text-muted-foreground">
                    <span>{p.date} · {p.payment_mode}{p.reference ? ` · ${p.reference}` : ""}{p.invoice_no ? ` · from ${p.invoice_no}` : ""} · {p.by}</span>
                    <span className="text-emerald-400 font-mono-sku">{fmt(p.amount)}</span>
                  </div>))}
              </div>
            )}
            <DialogFooter><Button type="submit" data-testid="inv-payment-submit" className="bg-emerald-500 hover:bg-emerald-600 text-white">Save {label}</Button></DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function StatusBadge({ status }) {
  const cls = status === "paid" ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
    : status === "partial" ? "text-sky-400 border-sky-500/30 bg-sky-500/10" : "text-amber-400 border-amber-500/40 bg-amber-500/10";
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${cls}`}>{status}</span>;
}
